import React, { useState } from 'react'
import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  VStack,
  HStack,
  useDisclosure,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
  Icon,
  Select,
} from '@chakra-ui/react'
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineSearch } from 'react-icons/ai'
import { MdCheck, MdClose, MdRefresh, MdAssignmentReturn } from 'react-icons/md'
import { useGetCancellations } from 'queries/cancellation.query'
import { ApproveModal } from './ApproveModal'
import { RejectModal } from './RejectModal'

export default function CancellationsTable() {
  const [page, setPage] = useState(1)
  const [perPage] = useState(15)
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selectedRequest, setSelectedRequest] = useState(null)

  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.400')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700')

  const { isOpen: isApproveOpen, onOpen: onOpenApprove, onClose: onCloseApprove } = useDisclosure()
  const { isOpen: isRejectOpen, onOpen: onOpenReject, onClose: onCloseReject } = useDisclosure()

  const { data: responseData, isLoading, refetch, isFetching } = useGetCancellations(
    page,
    perPage,
    statusFilter,
    search
  )

  const requests = responseData?.data?.data || []
  const pagination = responseData?.data || {}

  const handleOpenApprove = (req) => {
    setSelectedRequest(req)
    onOpenApprove()
  }

  const handleOpenReject = (req) => {
    setSelectedRequest(req)
    onOpenReject()
  }

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <Badge colorScheme="orange" px={2.5} py={0.5} borderRadius="full">Pending Review</Badge>
      case 'approved':
        return <Badge colorScheme="green" px={2.5} py={0.5} borderRadius="full">Approved & Refunded</Badge>
      case 'rejected':
        return <Badge colorScheme="red" px={2.5} py={0.5} borderRadius="full">Rejected</Badge>
      case 'refund_processing':
        return <Badge colorScheme="blue" px={2.5} py={0.5} borderRadius="full">Refund Processing</Badge>
      default:
        return <Badge px={2.5} py={0.5} borderRadius="full">{status}</Badge>
    }
  }

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      {/* Header bar */}
      <Flex px="25px" justify="space-between" mb="20px" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
        <VStack align="flex-start" spacing={1}>
          <HStack spacing={2.5}>
            <Icon as={MdAssignmentReturn} w="24px" height="24px" color="brand.500" />
            <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
              Ticket Cancellation Requests
            </Text>
          </HStack>
          <Text color={textColorSecondary} fontSize="sm">
            Review customer refund requests, calculate administrative penalty fees, and issue automated gateway refunds.
          </Text>
        </VStack>

        <HStack spacing={3} wrap="wrap">
          <InputGroup w={{ base: '100%', md: '240px' }} size="sm">
            <InputLeftElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputLeftElement>
            <Input
              borderRadius="12px"
              placeholder="Search order, name, email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </InputGroup>

          <Select
            size="sm"
            w="160px"
            borderRadius="12px"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </Select>

          <IconButton
            size="sm"
            aria-label="Refresh"
            icon={<MdRefresh />}
            borderRadius="10px"
            isLoading={isFetching}
            onClick={() => refetch()}
          />
        </HStack>
      </Flex>

      {/* Table Content */}
      {isLoading ? (
        <SkeletonTable />
      ) : requests.length === 0 ? (
        <Flex justify="center" align="center" direction="column" py="60px">
          <Icon as={MdAssignmentReturn} w="48px" height="48px" color="gray.300" mb={3} />
          <Text fontSize="lg" fontWeight="semibold" color={textColor}>
            No Cancellation Requests Found
          </Text>
          <Text fontSize="xs" color={textColorSecondary} mt={1}>
            {search || statusFilter ? 'Try clearing filters or search terms.' : 'No customer cancellation requests have been submitted yet.'}
          </Text>
        </Flex>
      ) : (
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead bg={TheadBackground}>
            <Tr>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">REQ ID / DATE</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">ORDER & TICKET</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">CUSTOMER</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">PRICING & PENALTY</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">NET REFUND</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px">STATUS</Th>
              <Th borderColor={borderColor} color="gray.400" fontSize="11px" textAlign="right">ACTIONS</Th>
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((row) => (
              <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }} transition="background 0.2s">
                <Td borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="bold" color={textColor}>
                    #{row.id}
                  </Text>
                  <Text fontSize="10px" color={textColorSecondary}>
                    {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="bold" fontFamily="mono" color="brand.500">
                    {row.order?.order_number || '—'}
                  </Text>
                  <Text fontSize="11px" fontFamily="mono" color={textColorSecondary}>
                    #{row.ticket?.ticket_code || '—'}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  <Text fontSize="xs" fontWeight="semibold" color={textColor}>
                    {row.customer_name}
                  </Text>
                  <Text fontSize="10px" color={textColorSecondary}>
                    {row.customer_email}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  <Text fontSize="xs" color={textColor}>
                    Orig: €{Number(row.original_price).toFixed(2)}
                  </Text>
                  <Text fontSize="10px" color="red.500">
                    Fee: -€{Number(row.penalty_amount).toFixed(2)} ({row.penalty_percent}%)
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  <Text fontSize="sm" fontWeight="bold" color="green.500">
                    €{Number(row.refundable_amount).toFixed(2)}
                  </Text>
                </Td>

                <Td borderColor={borderColor}>
                  {getStatusBadge(row.status)}
                </Td>

                <Td borderColor={borderColor} textAlign="right">
                  {row.status === 'pending' ? (
                    <HStack spacing={2} justify="flex-end">
                      <Tooltip label="Approve cancellation and execute gateway refund">
                        <Button
                          size="xs"
                          colorScheme="green"
                          borderRadius="8px"
                          leftIcon={<MdCheck />}
                          onClick={() => handleOpenApprove(row)}
                        >
                          Approve
                        </Button>
                      </Tooltip>
                      <Tooltip label="Decline cancellation request">
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="outline"
                          borderRadius="8px"
                          leftIcon={<MdClose />}
                          onClick={() => handleOpenReject(row)}
                        >
                          Decline
                        </Button>
                      </Tooltip>
                    </HStack>
                  ) : row.status === 'approved' ? (
                    <Tooltip label={row.refund_transaction_id ? `Gateway Refund ID: ${row.refund_transaction_id}` : 'Approved'}>
                      <Badge colorScheme="green" variant="subtle" fontSize="10px">
                        Refunded ✓
                      </Badge>
                    </Tooltip>
                  ) : (
                    <Tooltip label={row.admin_notes ? `Reason: ${row.admin_notes}` : 'Declined'}>
                      <Badge colorScheme="red" variant="subtle" fontSize="10px">
                        Declined ✕
                      </Badge>
                    </Tooltip>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {/* Pagination Footer */}
      {pagination.total > perPage && (
        <Flex px="25px" py="15px" justify="space-between" align="center" borderTop="1px solid" borderColor={borderColor}>
          <Text fontSize="xs" color={textColorSecondary}>
            Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} requests
          </Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              isDisabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              borderRadius="8px"
            >
              Previous
            </Button>
            <Text fontSize="xs" fontWeight="bold" px={2}>
              Page {page} of {pagination.last_page || 1}
            </Text>
            <Button
              size="xs"
              isDisabled={page >= (pagination.last_page || 1)}
              onClick={() => setPage((p) => p + 1)}
              borderRadius="8px"
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Approve Modal */}
      <ApproveModal
        isOpen={isApproveOpen}
        onClose={onCloseApprove}
        cancellation={selectedRequest}
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={isRejectOpen}
        onClose={onCloseReject}
        cancellation={selectedRequest}
      />
    </Card>
  )
}
